import axios from 'axios';
import endpoints from '../constants/endpoints';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 15000,
  withCredentials: true, // Enable cookies for refresh tokens
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Attempt to refresh token
        const refreshToken = localStorage.getItem('refreshToken');
        const response = await axios.post(
          endpoints.auth.refreshToken,
          { refreshToken },
          { withCredentials: true }
        );

        const { accessToken } = response.data;
        localStorage.setItem('accessToken', accessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - logout user
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// API methods
export const apiClient = {
  // Authentication
  login: (credentials) => api.post(endpoints.auth.login, credentials),
  register: (userData) => api.post(endpoints.auth.register, userData),
  logout: () => api.post(endpoints.auth.logout),
  refreshToken: (refreshToken) => api.post(endpoints.auth.refreshToken, { refreshToken }),
  getCaptcha: () => api.get(endpoints.auth.captcha),
  verifyCaptcha: (token, answer) => api.post(endpoints.auth.verifyCaptcha, { token, answer }),
  
  // User
  getProfile: () => api.get(endpoints.user.profile),
  updateProfile: (data) => api.put(endpoints.user.updateProfile, data),
  uploadAvatar: (formData) => api.post(endpoints.user.uploadAvatar, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  
  // Programs
  getPrograms: (params) => api.get(endpoints.programs.list, { params }),
  getProgramDetails: (id) => api.get(endpoints.programs.details(id)),
  enrollProgram: (id) => api.post(endpoints.programs.enroll(id)),
  getEnrolledPrograms: () => api.get(endpoints.programs.enrolled),
  getUpcomingPrograms: () => api.get(endpoints.programs.upcoming),
  getFeaturedPrograms: () => api.get(endpoints.programs.featured),
  
  // Attendance
  getAttendance: (programId, params) => api.get(endpoints.attendance.list(programId), { params }),
  markAttendance: (data) => api.post(endpoints.attendance.mark, data),
  exportAttendance: (programId) => api.get(endpoints.attendance.export(programId), {
    responseType: 'blob',
  }),
  getQRCode: (sessionId) => api.get(endpoints.attendance.qrCode(sessionId)),
  
  // Notifications
  getNotifications: (params) => api.get(endpoints.notifications.list, { params }),
  markNotificationRead: (id) => api.put(endpoints.notifications.markRead(id)),
  markAllNotificationsRead: () => api.put(endpoints.notifications.markAllRead),
  deleteNotification: (id) => api.delete(endpoints.notifications.delete(id)),
  
  // Resources
  uploadResource: (formData) => api.post(endpoints.resources.upload, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  downloadResource: (id) => api.get(endpoints.resources.download(id), {
    responseType: 'blob',
  }),
};

export default api;
